import type {
  AdminClient,
  AuditChainHead,
  AuditChainTable,
  AuditChainVerdict,
} from '@apx/admin-sdk';
import { icon } from '../icons.js';
import { fmtDateTime } from '../format.js';
import { showToast } from '../toast.js';
import { html, infoModal } from '../modal.js';
import { exportXlsx } from '../xlsx.js';

/**
 * Audit hash-chain dashboard.
 *
 * Lists every (integratorId × tableName) chain head with rowCount + lastHash,
 * and lets the admin trigger a server-side verification walk per chain.
 * Verdict renders inline next to the row so multiple chains can be checked
 * in one sitting.
 */

export interface AuditChainsPageDeps {
  client: AdminClient;
}

interface RowState {
  head: AuditChainHead;
  status: 'idle' | 'verifying' | 'ok' | 'broken';
  verdict?: AuditChainVerdict;
}

export async function renderAuditChainsPage(
  root: HTMLElement,
  deps: AuditChainsPageDeps,
): Promise<void> {
  root.innerHTML = `
    <div class="page-header">
      <h1>Audit hash chains</h1>
      <p class="muted">
        Every audit table (RoundAudit, WalletTransaction, AdminAudit) is chained per-integrator.
        Tampering any historic row breaks the chain at that point.
        Click <b>Verify</b> to walk the chain server-side and confirm row hashes match what we'd recompute.
      </p>
    </div>

    <div id="ac-content"><p class="muted">Loading chain heads…</p></div>
  `;

  const content = root.querySelector<HTMLDivElement>('#ac-content');
  if (!content) return;

  let rows: RowState[] = [];
  try {
    const { heads } = await deps.client.listAuditChainHeads();
    rows = heads.map((h) => ({ head: h, status: 'idle' as const }));
  } catch (err) {
    content.innerHTML = `<p class="error">Failed to load chain heads: ${escapeHtml(describeErr(err))}</p>`;
    return;
  }

  function openChainDetail(r: RowState): void {
    const kv = (k: string, v: string): ReturnType<typeof html> =>
      html`<div><div class="detail-k">${k}</div><div class="detail-v">${v}</div></div>`;
    const v = r.verdict;
    const verdictLine = !v
      ? html`<p class="muted">Not verified this session — click <b>Verify</b> on the row to walk the chain.</p>`
      : v.ok
        ? html`<p style="color:var(--success)"><b>VALID</b> — ${String(v.count)} row${v.count === 1 ? '' : 's'} verified, hashes match.</p>`
        : html`<p style="color:var(--error)"><b>BROKEN</b> at row <code>${v.brokenAtRowId}</code> — ${v.reason}</p>`;
    infoModal({
      title: `${r.head.tableName} chain`,
      subtitle: `${r.head.rowCount} rows · updated ${fmtDateTime(r.head.updatedAt)}`,
      wide: true,
      body: html`
        <div class="detail-grid">
          ${kv('Integrator id', r.head.integratorId)}
          ${kv('Table', r.head.tableName)}
          ${kv('Row count', String(r.head.rowCount))}
          ${kv('Last row id', r.head.lastRowId)}
          ${kv('Updated', fmtDateTime(r.head.updatedAt))}
        </div>
        <div class="detail-section-h">Last hash</div>
        <pre class="detail-json">${r.head.lastHash}</pre>
        <div class="detail-section-h">Verification</div>
        ${verdictLine}
      `,
    });
  }

  function paint(): void {
    if (rows.length === 0) {
      content!.innerHTML = `<p class="muted">No chains yet — write an audit row to bootstrap one.</p>`;
      return;
    }
    content!.innerHTML = `
      <div class="row" style="justify-content:flex-end;margin:0 0 10px">
        <button id="ac-export" class="ghost">Export Excel</button>
      </div>
      <table class="data" style="width:100%">
        <thead>
          <tr>
            <th>Integrator</th>
            <th>Table</th>
            <th>Rows</th>
            <th>Last row id</th>
            <th>Last hash</th>
            <th>Updated</th>
            <th></th>
            <th>Verdict</th>
          </tr>
        </thead>
        <tbody>
          ${rows
            .map(
              (r, i) => `
            <tr data-row-i="${i}" style="cursor:pointer">
              <td class="mono">${shortId(r.head.integratorId)}</td>
              <td><span class="pill">${escapeHtml(r.head.tableName)}</span></td>
              <td>${r.head.rowCount}</td>
              <td class="mono">${shortId(r.head.lastRowId)}</td>
              <td class="mono" title="${escapeHtml(r.head.lastHash)}">${shortHash(r.head.lastHash)}</td>
              <td>${fmtDateTime(r.head.updatedAt)}</td>
              <td><button class="ghost" data-i="${i}" ${r.status === 'verifying' ? 'disabled' : ''}>
                ${r.status === 'verifying' ? 'Verifying…' : 'Verify'}
              </button></td>
              <td>${renderVerdict(r)}</td>
            </tr>
          `,
            )
            .join('')}
        </tbody>
      </table>
    `;
    content!.querySelectorAll<HTMLButtonElement>('button[data-i]').forEach((btn) => {
      btn.addEventListener('click', (ev) => {
        ev.stopPropagation();
        const i = Number(btn.getAttribute('data-i'));
        void verifyOne(i);
      });
    });
    content!.querySelectorAll<HTMLTableRowElement>('tr[data-row-i]').forEach((tr) => {
      tr.addEventListener('click', (ev) => {
        if ((ev.target as HTMLElement).closest('button')) return;
        const r = rows[Number(tr.getAttribute('data-row-i'))];
        if (r) openChainDetail(r);
      });
    });
    content!.querySelector<HTMLButtonElement>('#ac-export')?.addEventListener('click', () => {
      if (rows.length === 0) {
        showToast('No chains to export.', 'warn');
        return;
      }
      const stamp = new Date().toISOString().replace(/[:.]/g, '-');
      exportXlsx(
        `apx-audit-chains-${stamp}.xlsx`,
        rows.map((r) => ({
          integratorId: r.head.integratorId,
          tableName: r.head.tableName,
          rowCount: r.head.rowCount,
          lastRowId: r.head.lastRowId,
          lastHash: r.head.lastHash,
          updatedAt: r.head.updatedAt,
          verdict: r.verdict ? (r.verdict.ok ? 'valid' : 'broken') : 'unverified',
        })),
      );
      showToast(`Exported ${rows.length} chains.`, 'success');
    });
  }

  async function verifyOne(i: number): Promise<void> {
    const r = rows[i];
    if (!r) return;
    r.status = 'verifying';
    paint();
    try {
      const res = await deps.client.verifyAuditChain(
        r.head.integratorId,
        r.head.tableName as AuditChainTable,
      );
      r.verdict = res.verdict;
      r.status = res.verdict.ok ? 'ok' : 'broken';
    } catch (err) {
      showToast(`Verify failed: ${describeErr(err)}`, 'error');
      r.status = 'idle';
    }
    paint();
  }

  paint();
}

function renderVerdict(r: RowState): string {
  if (r.status === 'idle' || r.status === 'verifying') return '<span class="muted">—</span>';
  const v = r.verdict;
  if (!v) return '<span class="muted">—</span>';
  if (v.ok) {
    return `<span class="pill ok" style="display:inline-flex;align-items:center;gap:4px">${icon('check', { size: 12 })} ${v.count} row${v.count === 1 ? '' : 's'} verified</span>`;
  }
  return `<span class="pill error" title="${escapeHtml(v.brokenAtRowId)}" style="display:inline-flex;align-items:center;gap:4px;flex-wrap:wrap">
    ${icon('x', { size: 12 })} broken at row ${shortId(v.brokenAtRowId)}
    <span class="muted">${escapeHtml(v.reason)}</span>
  </span>`;
}

function shortId(id: string): string {
  return id.length > 12 ? `${id.slice(0, 8)}…` : id;
}

function shortHash(h: string): string {
  return h.length > 16 ? `${h.slice(0, 12)}…` : h;
}

function escapeHtml(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

function describeErr(err: unknown): string {
  return err instanceof Error ? err.message : String(err);
}
